import { Image, Text, View } from "@react-pdf/renderer";
import styles from "./styles";
import { TrueFalseQuestionType } from "../types";
import { t } from "i18next";

type Props = {
  question: TrueFalseQuestionType;
  showAnswer: boolean;
  questionIndex: number;
};

export default function TrueFalseQuestion({
  question,
  showAnswer,
  questionIndex,
}: Props) {
  const { body, answer, point, mediaUrl, image, learningOutcome } = question;
  let imageFile: string | Buffer | undefined;
  if (image && image.startsWith("data:image")) {
    const base64 = image.split(",")[1];
    const buff = Buffer.from(base64, "base64");
    imageFile = buff;
  } else if (image) {
    imageFile = image;
  } else if (mediaUrl) {
    imageFile = mediaUrl;
  }

  const renderMarkdownText = (text: string = "") => {
    // Insert a zero-width space between numbers and periods to prevent PDF renderer from treating them as list items
    // Unicode character U+200B (zero-width space) will be invisible but break the list formatting
    text = text.replace(/(\d+)\./g, "$1\u200B.");

    // Split text to capture both bold (**text**) and italic (*text*) patterns
    const parts = text.split(/(\*\*.*?\*\*|\*[^*]+?\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <Text key={index} style={[styles.questionBody, styles.textBold]}>
            {part.slice(2, -2)}
          </Text>
        );
      } else if (part.startsWith("*") && part.endsWith("*")) {
        return (
          <Text key={index} style={[styles.questionBody, styles.textItalic]}>
            {part.slice(1, -1)}
          </Text>
        );
      }
      return part ? (
        <Text key={index} style={styles.questionBody}>
          {part}
        </Text>
      ) : null;
    });
  };

  return (
    <View style={[styles.questionContainerH]} wrap={false}>
      <View style={styles.questionContainerV}>
        <Text style={styles.questionNumber}>{`${questionIndex + 1}. `}</Text>
        <View style={styles.questionContainerH}>
          {imageFile && <Image src={imageFile} style={styles.questionImage} />}
          <Text style={styles.questionBody}>
            {renderMarkdownText(body)}
            <Text style={styles.questionNumber}>
              {" "}
              ({point} {t("pdf_renderer.point")}){" "}
            </Text>
          </Text>
          <View style={styles.trueFalseOptionsContainer}>
            <View style={styles.trueFalseOptionContainer}>
              <View
                style={[
                  styles.trueFalseCheckbox,
                  {
                    backgroundColor: showAnswer && answer ? "black" : "white",
                  },
                ]}
              />
              <Text style={styles.questionNumber}>
                {t("pdf_renderer.t_true")}
              </Text>
            </View>
            <View style={styles.trueFalseOptionContainer}>
              <View
                style={[
                  styles.trueFalseCheckbox,
                  {
                    backgroundColor: showAnswer && !answer ? "black" : "white",
                  },
                ]}
              />
              <Text style={styles.questionNumber}>
                {t("pdf_renderer.f_false")}
              </Text>
            </View>
          </View>
        </View>
      </View>
      {showAnswer && learningOutcome && (
        <Text style={[styles.answer, styles.questionBody]}>
          {t("pdf_renderer.outcome")}: {renderMarkdownText(learningOutcome)}
        </Text>
      )}
    </View>
  );
}
